/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Box,
	Image,
	Drawer as NativeDrawer,
	Text,
} from '@mantine/core';

// Utils
import {
	convertProduction,
	getMintStatusLabel,
	getMintStatusType,
	truncateString,
} from '@utils/functions';

// Components
import { MintStatus } from '@components/atoms/MintStatus';
import { Tooltip } from '@components/atoms/Tooltip';
import { VerifiedButton } from '@components/atoms/VerifiedButton';

// Style
import classes from './DrawerProduction.module.css';

// Icons
import ProductionLogo from '@assets/images/production-logo.svg';

// Icons
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerProduction = (props: {
	onClose: () => void;
	opened: boolean;
	data: any;
	position: any;
	fromstepper?: boolean;
}) => {
	/* ******************************** Media Query ******************************* */
	const {
		opened,
		onClose,

		fromstepper,
		...restProps
	} = props;
	const { t } = useTranslation();

	/* ******************************** RENDERING ******************************* */
	if (props.data)
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={ProductionLogo}
						/>
					</Box>

					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.project')}
						</Text>
						{props.data.project && (
							<Text className={classes.customTitle}>
								{props.data.project}
							</Text>
						)}
					</Box>
				</Box>

				{/* Body */}
				<Box className={classes.bodyDrawer}>
					{!isNil(props.data.organization) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={props.data.organization.name}
								isActive={
									props.data.organization.name.length > 20
								}
							>
								<Text className={classes.value}>
									{truncateString(
										props.data.organization.name,
										20
									)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{!isNil(props.data.entity) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.producer')}
							</Text>
							<Tooltip
								label={props.data.entity.name}
								isActive={
									props.data.entity.name.length > 20
								}
							>
								<Text className={classes.value}>
									{truncateString(
										props.data.entity.name,
										20
									)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{!isNil(props.data.quantity) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.quantity')}
							</Text>
							<Text className={classes.value}>
								{convertProduction(
									props.data.quantity,
									props.data.unit
								)}
							</Text>
						</Box>
					)}
					{!isNil(props.data.startDate) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.dateStart')}
							</Text>
							<Text className={classes.value}>
								{props.data.startDate}
							</Text>
						</Box>
					)}
					{!isNil(props.data.endDate) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.dateEnd')}
							</Text>
							<Text className={classes.value}>
								{props.data.endDate}
							</Text>
						</Box>
					)}

					{!isNil(props.data.fcApplicable) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.fcApplicable')}
							</Text>
							<Text className={classes.value}>
								{props.data.fcApplicable}
							</Text>
						</Box>
					)}

					{!isNil(props.data.tokenEquivalent) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.tokenEquivalent')}
							</Text>
							<Text className={classes.value}>
								{props.data.tokenEquivalent}
							</Text>
						</Box>
					)}
					{!isNil(props.data.project) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.status')}
							</Text>
							<Text className={classes.value}>
								<VerifiedButton
									data-testid="P2DlDXR7_Z5jdHi6B-button"
									readOnly={true}
									isverified={props.data.verified}
								>
									{props.data.verified
										? t('global.verified')
										: t('global.notVerified')}
								</VerifiedButton>
							</Text>
						</Box>
					)}
					<Box className={classes.row}>
						<Text className={classes.label}>
							{t('global.mint')}
						</Text>
						<MintStatus
							type={getMintStatusType(
								props.data?.mintStatus
							)}
						>
							{props.data?.mintStatus
								? getMintStatusLabel(
										props.data?.mintStatus
										// eslint-disable-next-line no-mixed-spaces-and-tabs
									)
								: t('global.none')}
						</MintStatus>
					</Box>
				</Box>

				{/* Footer */}
				{!fromstepper && (
					<Box className={classes.footer}></Box>
				)}
			</NativeDrawer>
		);
};
